import { ENV } from '@/config/environment';

// Single source of truth for loading the Google Maps JS API. Replaces three
// near-identical loaders (MapWidget, MultiMarkerMap, useGoogleMapsAutocomplete)
// that each kept their own "loaded" flag — a latent desync bug.
//
// We always request both `places` (autocomplete) and `marker` (AdvancedMarkerElement)
// so any caller has what it needs regardless of which page loads the script first.
// libraries payload is not billed; only `new google.maps.Map()` / geocode / places
// requests are. `loading=async` is set so Google doesn't warn about sync loading.

const SCRIPT_ID = 'google-maps-script';
const LIBRARIES = 'places,marker';

let loadPromise: Promise<void> | null = null;

const isReady = () => !!window.google?.maps;

export function loadGoogleMaps(): Promise<void> {
  if (isReady()) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    if (!ENV.GOOGLE_MAPS.API_KEY) {
      reject(new Error('Google Maps API key not configured'));
      return;
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      const wait = () => (isReady() ? resolve() : setTimeout(wait, 100));
      wait();
      return;
    }

    const callbackName = `googleMapsCallback_${Date.now()}`;
    (window as any)[callbackName] = () => {
      delete (window as any)[callbackName];
      resolve();
    };

    // TODO(maps-language): no `language` param, so labels follow the browser's
    // Accept-Language — a technician with a Hebrew/Spanish device sees US street
    // names in that language. Append `&language=en` here and in MapWidget's
    // staticMapUrl. (`&region=US` would also bias geocoding — US-only, skip if not.)
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${ENV.GOOGLE_MAPS.API_KEY}` +
      `&libraries=${LIBRARIES}&loading=async&callback=${callbackName}`;
    script.onerror = () => {
      loadPromise = null; // allow retry
      delete (window as any)[callbackName];
      reject(new Error('Failed to load Google Maps API'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

export const isGoogleMapsReady = isReady;
